import { useCallback, useEffect, useRef, useState } from "react";
import matchService from "../../../../services/api/matchService";
import teamService from "../../../../services/api/teamService";
import { apiClient } from "../../../../services/api/apiService";
import { getSportConfigKey } from "../../../../config/sportsConfig";
import { resolveMatchCompletion } from "../utils/matchCompletionUtils";
import {
  buildResetScorePayload,
  buildScoreEventLogEntry,
  buildScorePayload,
  extractScoresFromMatchResponse,
  extractScoreVersion,
  isScoreVersionConflict,
} from "../utils/scoreActionUtils";
import {
  addToPeriodLines,
  buildLiveScoreData,
  clockElapsedMs,
  gameStateFromScoreData,
  getMatchClockState,
  getMatchScoreData,
  historyRowToFeedEvent,
  mergeFeedEvents,
  newClientEventId,
  normalizePeriods,
  parseClockState,
  periodFromScoreData,
  setSportPeriods,
} from "../utils/liveScoreState";
import { retryWrite } from "../utils/retryWrite";
import { enrichParticipantNames } from "../utils/matchDataUtils";
import useLiveMatchEventForm from "./useLiveMatchEventForm";

const MAX_STORED_EVENTS = 50;
const HISTORY_LIMIT = 100;

/** A user-facing message for a failed score write. */
const scoreErrorMessage = (err, fallback) =>
  isScoreVersionConflict(err)
    ? "The score was changed on another device. Showing the latest score; please re-enter your last change."
    : err?.message || fallback;
const EVENTS_KEY = (id) => `live_events_${id}`;

const loadPersistedEvents = (matchId) => {
  if (!matchId) return [];
  try {
    const raw = localStorage.getItem(EVENTS_KEY(matchId));
    if (raw) return JSON.parse(raw) || [];
  } catch {}
  return [];
};

const savePersistedEvents = (matchId, events) => {
  if (!matchId) return;
  try {
    // strip session-restore dividers before saving so they don't accumulate
    const toSave = events
      .filter((e) => e?.type !== "session_restore")
      .slice(0, MAX_STORED_EVENTS);
    localStorage.setItem(EVENTS_KEY(matchId), JSON.stringify(toSave));
  } catch {}
};

/** Line-score row label for set sports: volleyball plays sets, racket sports games. */
const periodUnitFor = (match) =>
  getSportConfigKey(match?.sport_name || match?.sportName) === "volleyball" ? "Set" : "Game";

const useLiveMatchData = ({
  matchId,
  socket,
  connected,
  isMatchActive,
  setIsMatchActive,
}) => {
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scoringTemplate, setScoringTemplate] = useState(null);

  const [currentScore, setCurrentScore] = useState({ participant1: 0, participant2: 0 });
  const [eventLog, setEventLog] = useState([]);
  const eventsRestoredRef = useRef(false); // prevents initial-empty overwrite

  // ── Score queue refs — prevent stale closures and concurrent API calls ────
  // scoreRef always holds the latest intended score (updated synchronously on
  // every +1 click, avoids React stale-closure issues in callbacks).
  const scoreRef = useRef({ participant1: 0, participant2: 0 });
  // One POST is in flight at a time. Every tap is queued in order (LS-B10) —
  // never coalesced — so each event and its description reaches the server.
  const scoreInflightRef = useRef(false);
  const scoreQueueRef    = useRef([]);
  // ── Multi-device sync (LS-B05) ────────────────────────────────────────────
  // The server counts live score writes in score_data.score_version. Every
  // write carries the version this device last saw; if another device scored
  // in between, the server answers 409 SCORE_VERSION_CONFLICT and we reload
  // its score instead of overwriting it.
  const scoreVersionRef  = useRef(0);

  // ── Server-held match state (LS-B07 / LS-B08) ─────────────────────────────
  // gameState: set / game state of volleyball, badminton, table tennis boards
  // periodInfo: { period, periodLabel } reported by timed boards (quarters, halves)
  // periods: line score rows of timed sports
  const gameStateRef  = useRef(null);
  const periodInfoRef = useRef(null);
  const periodsRef    = useRef(null);
  // The set state stored on the server when the page loaded — boards hydrate from it
  const [serverGameState, setServerGameState] = useState(null);
  // Score writes made from this device, newest last: { participant, points, eventId }
  const scoreEventStackRef = useRef([]);

  // ── Server match clock (LS-B09) ───────────────────────────────────────────
  const [clockState, setClockStateRaw] = useState(null);
  const clockStateRef = useRef(null);
  const setClockState = useCallback((raw) => {
    const parsed = parseClockState(raw);
    if (!parsed) return;
    clockStateRef.current = parsed;
    setClockStateRaw(parsed);
  }, []);

  /** Match clock within the current period (ms), or null when the sport has no clock yet. */
  const currentClockMs = useCallback(
    () => (clockStateRef.current ? Math.round(clockElapsedMs(clockStateRef.current)) : null),
    []
  );

  /** { period, periodLabel } of the play in progress. */
  const currentPeriodInfo = useCallback(() => {
    const gs = gameStateRef.current;
    if (gs?.currentGame) return { period: gs.currentGame, periodLabel: null };
    if (periodInfoRef.current?.period) return periodInfoRef.current;
    if (clockStateRef.current?.period) {
      return { period: clockStateRef.current.period, periodLabel: clockStateRef.current.periodLabel };
    }
    return { period: 1, periodLabel: null };
  }, []);

  // ── restore persisted events on mount ────────────────────────────────────
  useEffect(() => {
    if (!matchId) return;
    eventsRestoredRef.current = false;
    const saved = loadPersistedEvents(matchId);
    if (saved.length > 0) {
      const divider = {
        id: `session_restore_${Date.now()}`,
        type: "session_restore",
        timestamp: new Date().toLocaleTimeString(),
        description: "— session resumed —",
        hideParticipantName: true,
      };
      setEventLog([divider, ...saved.map((e) => ({ ...e, restored: true }))]);
    }
    eventsRestoredRef.current = true;
  }, [matchId]);

  // ── persist events whenever the log changes ───────────────────────────────
  useEffect(() => {
    if (!matchId || !eventsRestoredRef.current) return;
    if (eventLog.length === 0) return; // never overwrite storage with an empty array
    savePersistedEvents(matchId, eventLog);
  }, [eventLog, matchId]);

  /** Adopt the set / period state of a stored score_data. */
  const adoptScoreState = useCallback((scoreData) => {
    if (!scoreData) return null;
    const gs = gameStateFromScoreData(scoreData);
    if (gs) {
      gameStateRef.current = gs;
    } else {
      const info = periodFromScoreData(scoreData);
      if (info) periodInfoRef.current = info;
      if (Array.isArray(scoreData.periods)) periodsRef.current = normalizePeriods(scoreData.periods);
    }
    return gs;
  }, []);

  /**
   * The timeline stored on the server (LS-B12). It replaces the copy kept in
   * this browser, so a reload, a second device or a late viewer sees the same
   * feed; events made in this session but not yet stored are kept on top.
   */
  const loadEventHistory = useCallback(async (dbId) => {
    try {
      const response = await apiClient.get(`/websocket/matches/${dbId}/events`, {
        params: { limit: HISTORY_LIMIT },
      });
      const rows = response?.data?.data?.events;
      if (!Array.isArray(rows) || rows.length === 0) return;
      const history = rows.map(historyRowToFeedEvent).filter(Boolean);
      if (history.length === 0) return;
      setEventLog((prev) => {
        const sessionEvents = prev.filter((e) => !e.restored && e.type !== "session_restore");
        return mergeFeedEvents(history, sessionEvents, MAX_STORED_EVENTS);
      });
    } catch (err) {
      console.warn("Could not load the match timeline:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    if (!matchId) return;

    const loadMatchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const matchResponse = await matchService.getMatchById(matchId);
        const apiBaseUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:5000/api";
        const matchData = await enrichParticipantNames({
          matchData: matchResponse.data,
          matchId,
          apiBaseUrl,
        });

        setMatch(matchData);

        const initialScore = extractScoresFromMatchResponse(matchData);
        const safeScore = initialScore || { participant1: 0, participant2: 0 };
        setCurrentScore(safeScore);
        scoreRef.current = safeScore;
        scoreVersionRef.current = extractScoreVersion(matchData) ?? 0;
        setServerGameState(adoptScoreState(getMatchScoreData(matchData)));
        const clock = getMatchClockState(matchData);
        if (clock) setClockState(clock);

        if (matchData?.sport_id) {
          try {
            const templateResponse = await matchService.getScoringTemplate(matchData.sport_id);
            setScoringTemplate(templateResponse?.data);
          } catch (err) {
            console.error("Error loading scoring template:", err);
          }
        }

        setIsMatchActive(matchData?.status === "in_progress");
        setLoading(false);
        if (matchData?.id != null) loadEventHistory(matchData.id);
      } catch (err) {
        console.error("Error loading match data:", err);
        setError(err.message || "Failed to load match data");
        setLoading(false);
      }
    };

    loadMatchData();
  }, [matchId, setIsMatchActive, adoptScoreState, setClockState, loadEventHistory]);

  /** Fields every event carries: the period and the match clock (LS-B08). */
  const timingFields = useCallback(() => {
    const info = currentPeriodInfo();
    const clockMs = currentClockMs();
    return {
      period: info.period ?? 1,
      ...(info.periodLabel ? { periodLabel: info.periodLabel } : {}),
      ...(clockMs !== null ? { clockMs } : {}),
    };
  }, [currentClockMs, currentPeriodInfo]);

  const handleSendEvent = useCallback(
    async (eventType, payload = {}) => {
      if (!isMatchActive || !connected) {
        setError("Match must be active and WebSocket connected to send events");
        return;
      }

      try {
        setError(null);
        const timing = timingFields();
        const eventPayload = {
          eventType,
          eventData: {
            ...timing,
            ...payload,
            eventTime: Math.floor(Date.now() / 1000),
            // Callers may supply `period` (= current set number for volleyball etc.)
            period: payload.period ?? timing.period ?? 1,
            clientEventId: payload.clientEventId || newClientEventId(eventType),
          },
        };

        await apiClient.post(`/websocket/matches/${matchId}/event`, eventPayload);
      } catch (err) {
        console.error("❌ Error sending event:", err);
        setError(err.message || "Failed to send event");
      }
    },
    [connected, isMatchActive, matchId, setError, timingFields]
  );

  /**
   * Store the match clock on the server and broadcast it (LS-B09):
   * { running, elapsedMs, period, periodLabel }. Applied locally at once.
   */
  const postClock = useCallback(async ({ running, elapsedMs, period, periodLabel } = {}) => {
    const body = {
      running: !!running,
      elapsedMs: Math.max(0, Math.round(Number(elapsedMs) || 0)),
      period: Number(period) > 0 ? Number(period) : 1,
      ...(periodLabel ? { periodLabel } : {}),
    };
    setClockState({
      ...body,
      runningSince: body.running ? new Date().toISOString() : null,
      updatedAt: new Date().toISOString(),
    });
    if (!matchId) return;
    try {
      const response = await apiClient.post(`/websocket/matches/${matchId}/clock`, body);
      const stored = response?.data?.data?.clockState;
      if (stored) setClockState(stored);
    } catch (err) {
      console.warn("⚠️ Could not save the match clock:", err?.response?.data?.message || err?.message);
    }
  }, [matchId, setClockState]);

  const handleStartMatch = useCallback(async () => {
    try {
      setError(null);
      await matchService.startMatch(matchId);

      const response = await matchService.getMatchById(matchId);
      const updatedMatch = response.data;
      setMatch(updatedMatch);
      setIsMatchActive(true);

      if (socket && connected) {
        try {
          const trackingData = {
            tournament_id: updatedMatch.tournament_id,
            sport_id: updatedMatch.sport_id,
            participant_1_id: updatedMatch.participant1Id,
            participant_2_id: updatedMatch.participant2Id,
            status: "in_progress",
          };

          await apiClient.post(`/websocket/matches/${matchId}/start`, trackingData);
        } catch (wsErr) {
          console.error("❌ Error starting WebSocket tracking:", wsErr);
        }
      }
    } catch (err) {
      console.error("❌ Error starting match:", err);
      setError(err.message || "Failed to start match");
    }
  }, [connected, matchId, setError, setIsMatchActive, setMatch, socket]);

  /** Line score to store with the final result (sets / games or periods). */
  const finalPeriods = useCallback((score) => {
    if (gameStateRef.current) return setSportPeriods(gameStateRef.current, score, periodUnitFor(match));
    return periodsRef.current ? normalizePeriods(periodsRef.current) : null;
  }, [match]);

  /** Tell viewers the match ended — only after the result was committed (LS-B13). */
  const broadcastMatchEnd = useCallback(async (finalData) => {
    if (!socket || !connected) return;
    try {
      await apiClient.post(`/websocket/matches/${matchId}/end`, finalData);
    } catch (wsErr) {
      console.error("❌ Error ending WebSocket tracking:", wsErr);
    }
  }, [connected, matchId, socket]);

  const handleCompleteMatch = useCallback(async (completionOverride = null) => {
    try {
      const completionResult = completionOverride || resolveMatchCompletion({
        match,
        currentScore,
      });

      if (completionResult.cancelled) {
        return;
      }

      const {
        winner,
        participant1Score,
        participant2Score,
        gameScores,          // present for multi-game sports (badminton Bo3/Bo5)
        penalties,           // football: penalty shootout result after a level game
      } = completionResult;

      setError(null);

      const isDraw = !winner;
      const periods = finalPeriods(currentScore);

      // For multi-game sports the final "score" is games-won, not point score.
      // For single-game sports it's the current point score.
      const baseScore = gameScores
        ? {
            participant1: participant1Score,
            participant2: participant2Score,
            gameScores,      // [{p1, p2}, …] one entry per completed game
          }
        : penalties
          ? { ...currentScore, penalties }
          : { ...currentScore };
      const finalScore = periods ? { ...baseScore, periods } : baseScore;

      // The result is committed first; match_ended is only broadcast once the
      // server accepted it, so viewers never see an end that did not happen.
      const completion = await matchService.completeMatch(matchId, {
        finalScore,
        winner_id: isDraw ? null : winner.id,
        winner_type: isDraw ? null : winner.type,
        result_type: isDraw ? "draw" : "normal",
      });

      // A captain's result waits for the other team: the match has not ended yet
      if (completion?.data?.pending_result) {
        const pending = await matchService.getMatchById(matchId);
        setMatch(pending.data);
        return;
      }

      setIsMatchActive(false);

      await broadcastMatchEnd({
        winner: isDraw ? null : winner,
        finalScore,
        result_type: isDraw ? "draw" : "normal",
      });

      const response = await matchService.getMatchById(matchId);
      setMatch(response.data);
    } catch (err) {
      console.error("❌ Error completing match:", err);
      setError(err.message || "Failed to complete match");
    }
  }, [
    broadcastMatchEnd,
    currentScore,
    finalPeriods,
    match,
    matchId,
    setError,
    setIsMatchActive,
    setMatch,
  ]);

  /** Keep the version the server stored for our write (never move backwards). */
  const rememberScoreVersion = useCallback((response) => {
    const version = extractScoreVersion(response?.data?.data);
    if (version !== null && version >= scoreVersionRef.current) {
      scoreVersionRef.current = version;
    }
  }, []);

  /** Load the authoritative score + version from the server (after a refused write). */
  const reloadServerScore = useCallback(async () => {
    try {
      const response = await matchService.getMatchById(matchId);
      const recoveredScores = extractScoresFromMatchResponse(response.data);
      if (recoveredScores) {
        setCurrentScore(recoveredScores);
        scoreRef.current = recoveredScores;
      }
      const version = extractScoreVersion(response.data);
      if (version !== null) {
        scoreVersionRef.current = version;
      }
      const gs = adoptScoreState(getMatchScoreData(response.data));
      if (gs) setServerGameState(gs);
    } catch (revertErr) {
      console.error("❌ Error reverting score:", revertErr);
    }
  }, [adoptScoreState, matchId]);

  /**
   * Adopt a score that came from the server (WebSocket broadcast or poll) as
   * the base for the next local write. Returns false — and changes nothing —
   * while a local write is pending (it is authoritative until it settles) or
   * when the update is older than the version we already have.
   */
  const syncServerScore = useCallback((score, version) => {
    if (!score) return false;
    if (scoreInflightRef.current || scoreQueueRef.current.length > 0) return false;
    const v = Number.isInteger(version) ? version : null;
    if (v !== null && v < scoreVersionRef.current) return false;
    scoreRef.current = { ...scoreRef.current, participant1: score.participant1 ?? scoreRef.current.participant1, participant2: score.participant2 ?? scoreRef.current.participant2 };
    if (v !== null) scoreVersionRef.current = v;
    return true;
  }, []);

  /**
   * A full score_data broadcast by another device: adopt its set / period
   * state (LS-B07) unless this device has a write pending. Returns the board
   * game state it carries, or null.
   */
  const adoptServerState = useCallback((scoreData) => {
    if (!scoreData || typeof scoreData !== "object") return null;
    if (scoreInflightRef.current || scoreQueueRef.current.length > 0) return null;
    const version = extractScoreVersion(scoreData);
    if (version !== null && version < scoreVersionRef.current) return null;
    return adoptScoreState(scoreData);
  }, [adoptScoreState]);

  /**
   * Set the board state that goes with every following score write:
   *   { gameState }              set sports, when a set / game ends
   *   { period, periodLabel }    timed sports, when the quarter / half changes
   */
  const setScoreContext = useCallback(({ gameState, period, periodLabel } = {}) => {
    if (gameState && Array.isArray(gameState.gameScores)) {
      gameStateRef.current = gameState;
    }
    if (Number(period) > 0) {
      periodInfoRef.current = { period: Number(period), periodLabel: periodLabel || null };
      periodsRef.current = addToPeriodLines(periodsRef.current, { period, label: periodLabel });
    }
  }, []);

  /** The score_data for a write, built at tap time (the queue keeps it as is). */
  const snapshotScoreData = useCallback((newScores) => buildLiveScoreData({
    score: newScores,
    gameState: gameStateRef.current,
    periodInfo: gameStateRef.current ? null : periodInfoRef.current,
    periods: gameStateRef.current ? null : periodsRef.current,
    periodUnit: periodUnitFor(match),
  }), [match]);

  /** Record a points change for `participant` in the line score of the current period. */
  const addPeriodPoints = useCallback((participant, delta) => {
    if (gameStateRef.current || !periodInfoRef.current?.period) return;
    periodsRef.current = addToPeriodLines(periodsRef.current, {
      period: periodInfoRef.current.period,
      label: periodInfoRef.current.periodLabel,
      side: participant,
      delta,
    });
  }, []);

  /**
   * Internal: send queued score writes to the server, one at a time and in
   * order. Each entry is { scoreData, eventData, participant, points, track }.
   */
  const flushScoreQueue = useCallback(async () => {
    if (scoreInflightRef.current) return;
    const entry = scoreQueueRef.current.shift();
    if (!entry) return;

    scoreInflightRef.current = true;
    try {
      const response = await apiClient.post(`/websocket/matches/${matchId}/score`, {
        scoreData: entry.scoreData,
        eventData: entry.eventData,
        scoreVersion: scoreVersionRef.current,
        expectedVersion: scoreVersionRef.current,
      });
      rememberScoreVersion(response);
      if (entry.track) {
        scoreEventStackRef.current.push({
          participant: entry.participant,
          points: entry.points,
          eventId: response?.data?.data?.scoreEvent?.eventId || null,
          clientEventId: entry.eventData?.clientEventId || null,
        });
        scoreEventStackRef.current = scoreEventStackRef.current.slice(-100);
      }
    } catch (err) {
      console.error("❌ Error updating score:", err);
      // Queued taps were computed from the rejected score; drop them rather
      // than send another stale absolute score.
      scoreQueueRef.current = [];
      setError(scoreErrorMessage(err, "Failed to update score"));
      // Revert to the server's last-known score so the UI is consistent.
      await reloadServerScore();
    } finally {
      scoreInflightRef.current = false;
      if (scoreQueueRef.current.length > 0) flushScoreQueue();
    }
  }, [matchId, reloadServerScore, rememberScoreVersion, setError]);

  /** Queue one score write (every tap, in order — LS-B10). */
  const enqueueScoreWrite = useCallback(({ newScores, participant, description, points, eventExtra = {}, track = true }) => {
    const base = buildScorePayload({ match, participant, newScores, description }) || {};
    const timing = timingFields();
    const eventData = {
      ...(base.eventData || {}),
      ...timing,
      eventTime: Math.floor(Date.now() / 1000),
      description,
      side: participant,
      points,
      clientEventId: eventExtra.clientEventId || newClientEventId("score"),
      ...eventExtra,
    };
    scoreQueueRef.current.push({
      scoreData: snapshotScoreData(newScores),
      eventData,
      participant,
      points,
      track: track && points > 0,
    });
    flushScoreQueue();
  }, [flushScoreQueue, match, snapshotScoreData, timingFields]);

  const handleScoreUpdate = useCallback((participant, points = 1) => {
    setError(null);

    // Compute new score from the ref (synchronous, no stale-closure risk).
    const newScores = {
      ...scoreRef.current,
      [participant]: (scoreRef.current[participant] || 0) + points,
    };
    scoreRef.current = newScores; // keep ref in sync before React state update
    addPeriodPoints(participant, points);

    setCurrentScore(newScores); // optimistic UI update

    const scoreEvent = buildScoreEventLogEntry({
      type: "score_increase",
      participant,
      newScores,
      match,
      extra: points > 1 ? { points } : undefined,
    });
    const clientEventId = newClientEventId("score");
    setEventLog((prev) => [
      { ...scoreEvent, points, clientEventId },
      ...prev.slice(0, MAX_STORED_EVENTS - 1),
    ]);

    const description = points > 1
      ? `+${points} — ${scoreEvent.description}`
      : scoreEvent.description;

    enqueueScoreWrite({ newScores, participant, description, points, eventExtra: { clientEventId } });
  }, [addPeriodPoints, enqueueScoreWrite, match, setCurrentScore, setError, setEventLog]);

  /** Save a player stat; retried on network / server errors, never silently lost (AS-B05). */
  const recordMetric = useCallback((payload) => {
    retryWrite(() => teamService.recordPlayerMetric(payload, { throwOnError: true }))
      .catch((err) => {
        console.error("❌ Player stat not saved:", err);
        setError(
          `The stat "${payload.metricKey}" could not be saved for this player` +
          ` (${err?.response?.data?.message || err?.message || "network error"}). The score is unaffected.`
        );
      });
  }, [setError]);

  const undoMetric = useCallback((payload) => {
    retryWrite(() => teamService.undoPlayerMetric(payload, { throwOnError: true }))
      .catch((err) => {
        console.error("❌ Player stat undo not saved:", err);
        setError(
          `Undoing the stat "${payload.metricKey}" failed` +
          ` (${err?.response?.data?.message || err?.message || "network error"}).`
        );
      });
  }, [setError]);

  /**
   * Advanced scoring update: increments score (if metric.affectsScore) AND
   * adds a rich advanced_score entry to the event log in a single operation.
   * Also broadcasts the event via WebSocket for remote viewers.
   *
   * advancedData.clientEventId  — optional id for this tap (generated otherwise)
   * advancedData.linkedEvent    — { playerUserId, metricKey, clientEventId } of the
   *                               fault stat that gave this (opponent) point; undone with it
   */
  const handleAdvancedScoreUpdate = useCallback(async (participant, advancedData) => {
    const clientEventId = advancedData.clientEventId || newClientEventId("adv");
    const pts = advancedData.affectsScore ? (advancedData.scorePoints || 1) : 0;
    try {
      setError(null);

      if (advancedData.affectsScore) {
        const newScores = {
          ...scoreRef.current,
          [participant]: (scoreRef.current[participant] || 0) + pts,
        };
        scoreRef.current = newScores;
        addPeriodPoints(participant, pts);
        setCurrentScore(newScores);

        enqueueScoreWrite({
          newScores,
          participant,
          description: `${advancedData.playerName} — ${advancedData.metricLabel}`,
          points: pts,
          eventExtra: {
            clientEventId: `${clientEventId}:score`,
            metricKey: advancedData.metricKey,
            playerUserId: advancedData.playerUserId || null,
          },
        });
      }

      // Rich event log entry — all advanced fields preserved
      const feedEvent = {
        id: clientEventId,
        clientEventId,
        type: 'advanced_score',
        participant,
        playerName:   advancedData.playerName,
        playerUserId: advancedData.playerUserId || null,
        metricKey:    advancedData.metricKey,
        metricLabel:  advancedData.metricLabel,
        typeLabel:    advancedData.typeLabel || null,
        affectsScore: advancedData.affectsScore,
        isPositive:   advancedData.isPositive,
        scorePoints:  pts,
        linkedEvent:  advancedData.linkedEvent || null,
        timestamp:    new Date().toLocaleTimeString(),
      };
      setEventLog((prev) => [feedEvent, ...prev.slice(0, MAX_STORED_EVENTS - 1)]);

      // Broadcast for remote viewers (non-fatal if this fails)
      try {
        await apiClient.post(`/websocket/matches/${matchId}/event`, {
          eventType: 'advanced_score',
          eventData: {
            ...advancedData,
            ...timingFields(),
            participant,
            participantKey: participant,
            side: participant,
            points: pts,
            scorePoints: pts,
            clientEventId,
            eventTime: Math.floor(Date.now() / 1000),
          },
        });
      } catch (broadcastErr) {
        console.warn('⚠️ Failed to broadcast advanced score event:', broadcastErr);
      }

      // Record the metric in player_stats — retried, a final failure is shown
      if (advancedData.playerUserId) {
        recordMetric({
          userId:      advancedData.playerUserId,
          matchId:     match?.id ?? matchId,
          teamId:      advancedData.teamId || null,
          sportId:     match?.sport_id || match?.sportId || null,
          metricKey:   advancedData.metricKey,
          affectsScore: advancedData.affectsScore,
          clientEventId,
        });
      }
    } catch (err) {
      console.error('❌ Error in advanced score update:', err);
      setError(err.message || 'Failed to update score');
      await reloadServerScore();
    }
  }, [addPeriodPoints, enqueueScoreWrite, match, matchId, recordMetric, reloadServerScore, setCurrentScore, setEventLog, setError, timingFields]);

  /**
   * Substitution: add "IN: x · OUT: y" to the feed and broadcast it to viewers.
   * sub = { inId, outId, inName, outName, teamName, period, clockMs }
   */
  const handleSubstitutionEvent = useCallback(async (participant, sub) => {
    const description = `IN: ${sub.inName} · OUT: ${sub.outName}`;
    const clientEventId = newClientEventId('sub');
    setEventLog((prev) => [{
      id: clientEventId,
      clientEventId,
      type: 'substitution',
      participant,
      participantName: sub.teamName,
      description,
      timestamp: new Date().toLocaleTimeString(),
    }, ...prev.slice(0, MAX_STORED_EVENTS - 1)]);

    try {
      const timing = timingFields();
      await apiClient.post(`/websocket/matches/${matchId}/event`, {
        eventType: 'substitution',
        eventData: {
          ...timing,
          participant,
          participantKey: participant,
          participantName: sub.teamName,
          description,
          inUserId: sub.inId,
          outUserId: sub.outId,
          eventTime: Math.floor(Date.now() / 1000),
          period: sub.period ?? timing.period ?? 1,
          ...(Number.isFinite(sub.clockMs) ? { clockMs: sub.clockMs } : {}),
          clientEventId,
        },
      });
    } catch (broadcastErr) {
      console.warn('⚠️ Failed to broadcast substitution:', broadcastErr);
    }
  }, [matchId, setEventLog, timingFields]);

  /**
   * −1 / undo for `participant` (LS-B11). When the last point for that side
   * came from an advanced-scoring tap, exactly that tap is undone: its stat
   * (same clientEventId, right player, right metric), its points (e.g. 3 for a
   * 3-pointer) and, for a point conceded on a fault, the fault stat too. The
   * score write voids the server event of the point it takes back.
   */
  const handleScoreDecrease = useCallback((participant, points = 1) => {
    setError(null);
    const currentVal = scoreRef.current[participant] || 0;

    if (currentVal <= 0) {
      setError("Score cannot be negative");
      return;
    }

    const lastScoringEvent = eventLog.find(
      (e) => e.participant === participant && e.type === 'advanced_score' && e.affectsScore === true
    );
    const eventPts = Number(lastScoringEvent?.scorePoints) > 0 ? Number(lastScoringEvent.scorePoints) : null;
    const deduct = Math.min(eventPts ?? points, currentVal);
    const matchDbId = match?.id ?? matchId;

    if (lastScoringEvent) {
      const linked = lastScoringEvent.linkedEvent;
      const removeIds = new Set([lastScoringEvent.id, linked?.clientEventId].filter(Boolean));
      setEventLog((prev) => prev.filter((e) => !removeIds.has(e.id)));
      if (lastScoringEvent.playerUserId) {
        undoMetric({
          userId:        lastScoringEvent.playerUserId,
          matchId:       matchDbId,
          metricKey:     lastScoringEvent.metricKey,
          clientEventId: lastScoringEvent.clientEventId || lastScoringEvent.id,
        });
      }
      if (linked?.playerUserId && linked.metricKey) {
        undoMetric({
          userId:        linked.playerUserId,
          matchId:       matchDbId,
          metricKey:     linked.metricKey,
          clientEventId: linked.clientEventId,
        });
      }
    }

    // The point being taken back: the latest write this device made for that side
    const stack = scoreEventStackRef.current;
    let voidsEventId = null;
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i].participant === participant) {
        voidsEventId = stack[i].eventId;
        stack.splice(i, 1);
        break;
      }
    }

    const newScores = { ...scoreRef.current, [participant]: currentVal - deduct };
    scoreRef.current = newScores;
    addPeriodPoints(participant, -deduct);
    setCurrentScore(newScores);

    const scoreEvent = buildScoreEventLogEntry({ type: "score_decrease", participant, newScores, match });
    setEventLog((prev) => [{ ...scoreEvent, points: -deduct }, ...prev.slice(0, MAX_STORED_EVENTS - 1)]);

    enqueueScoreWrite({
      newScores,
      participant,
      description: `Score correction -${deduct} for ${participant}`,
      points: -deduct,
      eventExtra: { undo: true, ...(voidsEventId ? { voidsEventId } : {}) },
      track: false,
    });
  }, [addPeriodPoints, enqueueScoreWrite, eventLog, match, matchId, setCurrentScore, setError, setEventLog, undoMetric]);

  /**
   * Reset the point score to 0–0.
   * options.eventType === "game_start" marks the reset as the start of a new
   * game/set (multi-game sports) so the feed shows e.g. "Game 2 started"
   * instead of a "score reset". options.period is the new game number.
   * A plain reset only changes the score — the match clock keeps running (LS-B09).
   */
  const handleResetScore = useCallback(async (options) => {
    const opts = options && typeof options === "object" && options.eventType ? options : {};
    try {
      setError(null);
      const newScores = { participant1: 0, participant2: 0 };
      scoreRef.current = newScores;
      setCurrentScore(newScores);
      if (opts.eventType !== "game_start" && !gameStateRef.current) {
        // The line score of a timed sport starts again with the score
        periodsRef.current = periodInfoRef.current?.period
          ? addToPeriodLines([], { period: periodInfoRef.current.period, label: periodInfoRef.current.periodLabel })
          : null;
      }

      const base = buildResetScorePayload({
        match,
        newScores,
        description: opts.description,
        period: opts.period,
      }) || {};

      const response = await apiClient.post(`/websocket/matches/${matchId}/score`, {
        ...base,
        scoreData: snapshotScoreData(newScores),
        eventData: {
          ...(base.eventData || {}),
          ...timingFields(),
          ...(opts.period ? { period: opts.period } : {}),
          clientEventId: newClientEventId(opts.eventType === "game_start" ? "game" : "reset"),
        },
        scoreVersion: scoreVersionRef.current,
        expectedVersion: scoreVersionRef.current,
      });
      rememberScoreVersion(response);

      const scoreEvent = buildScoreEventLogEntry({
        type: opts.eventType === "game_start" ? "game_start" : "score_reset",
        newScores,
        match,
        description: opts.description,
      });

      setEventLog((prev) => [scoreEvent, ...prev.slice(0, MAX_STORED_EVENTS - 1)]);
    } catch (err) {
      console.error("❌ Error resetting score:", err);
      setError(scoreErrorMessage(err, "Failed to reset score"));
      if (isScoreVersionConflict(err)) {
        await reloadServerScore();
      }
    }
  }, [match, matchId, reloadServerScore, rememberScoreVersion, setCurrentScore, setEventLog, setError, snapshotScoreData, timingFields]);

  /**
   * True while a local score update is in flight or queued. Editors must not
   * let the server's echo of an older score overwrite the newer local score
   * (otherwise rapid +1/+2/+3 taps appear to be dropped — BUG-67).
   */
  const isScoreSyncPending = useCallback(
    () => scoreInflightRef.current || scoreQueueRef.current.length > 0,
    []
  );

  const {
    showEventForm,
    setShowEventForm,
    eventData,
    setEventData,
    handleAddEvent,
    getEventTypes,
  } = useLiveMatchEventForm({
    scoringTemplate,
    handleSendEvent,
    setError,
  });

  /** Cancel an in-progress or scheduled match (host/admin only). */
  const handleCancelMatch = useCallback(async () => {
    try {
      setError(null);
      await matchService.cancelMatch(matchId);
      setIsMatchActive(false);
      const response = await matchService.getMatchById(matchId);
      setMatch(response.data);
    } catch (err) {
      console.error("❌ Error cancelling match:", err);
      setError(err.message || "Failed to cancel match");
    }
  }, [matchId, setError, setIsMatchActive, setMatch]);

  /**
   * Mark a match as completed due to forfeit (LS-B18).
   * forfeitingParticipant: 'participant1' | 'participant2' (the side that forfeits)
   *
   * Set sports store the sets / games won (and each completed set), never the
   * points of the set in play; the winner is the side that did not forfeit.
   */
  const handleForfeitMatch = useCallback(async (forfeitingParticipant) => {
    try {
      setError(null);
      const p1Id   = match?.participant1Id   || match?.participant_1_id;
      const p2Id   = match?.participant2Id   || match?.participant_2_id;
      const p1Type = match?.participant1Type || match?.participant_1_type || 'team';
      const p2Type = match?.participant2Type || match?.participant_2_type || 'team';

      const winnerId   = forfeitingParticipant === 'participant1' ? p2Id   : p1Id;
      const winnerType = forfeitingParticipant === 'participant1' ? p2Type : p1Type;

      const gs = gameStateRef.current;
      const points = { participant1: scoreRef.current.participant1 || 0, participant2: scoreRef.current.participant2 || 0 };
      const periods = finalPeriods(points);
      const finalScore = gs
        ? {
            participant1: gs.gamesWon?.p1 || 0,
            participant2: gs.gamesWon?.p2 || 0,
            gameScores: (gs.gameScores || []).map((g) => ({ p1: g.p1, p2: g.p2 })),
            // The set in play when the match stopped, for the record only
            unfinishedSet: points,
            ...(periods ? { periods } : {}),
          }
        : { ...points, ...(periods ? { periods } : {}) };

      const completion = await matchService.completeMatch(matchId, {
        finalScore,
        score_data: finalScore,
        winner_id:  winnerId,
        winner_type: winnerType,
        result_type: 'forfeit',
        forfeiting_participant: forfeitingParticipant === 'participant1' ? 1 : 2,
      });

      // A captain's result waits for the other team: the match has not ended yet
      if (completion?.data?.pending_result) {
        const pending = await matchService.getMatchById(matchId);
        setMatch(pending.data);
        return;
      }

      setIsMatchActive(false);
      await broadcastMatchEnd({
        winner: { id: winnerId, type: winnerType },
        finalScore,
        result_type: 'forfeit',
      });
      const response = await matchService.getMatchById(matchId);
      setMatch(response.data);
    } catch (err) {
      console.error("❌ Error forfeiting match:", err);
      setError(err.message || "Failed to record forfeit");
    }
  }, [broadcastMatchEnd, finalPeriods, match, matchId, setError, setIsMatchActive, setMatch]);

  return {
    match,
    setMatch,
    loading,
    error,
    setError,
    showEventForm,
    setShowEventForm,
    scoringTemplate,
    eventData,
    setEventData,
    currentScore,
    setCurrentScore,
    eventLog,
    setEventLog,
    handleStartMatch,
    handleCompleteMatch,
    handleScoreUpdate,
    handleAdvancedScoreUpdate,
    handleSubstitutionEvent,
    handleScoreDecrease,
    handleResetScore,
    handleCancelMatch,
    handleForfeitMatch,
    handleAddEvent,
    handleSendEvent,
    getEventTypes,
    isScoreSyncPending,
    syncServerScore,
    adoptServerState,
    setScoreContext,
    serverGameState,
    clockState,
    setClockState,
    postClock,
    currentClockMs,
  };
};

export default useLiveMatchData;
